import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BookingService } from "@/services/BookingService";
import type { Booking } from "@/types/Booking";

export type CancelBookingParams = {
  bookingId: string;
  waive?: boolean;
  waiveNote?: string;
};

/**
 * React Query mutation hook for cancelling a booking.
 *
 * On success, invalidates all booking-related queries so the calendar and any open booking
 * views reflect the cancellation immediately. Without this the stale cache sat for up to
 * `staleTime` (2 minutes in prod) after a cancel — users saw their cancelled booking linger.
 */
export function useCancelBooking() {
  const queryClient = useQueryClient();
  const bookingService = new BookingService();

  return useMutation<Booking, Error, CancelBookingParams>({
    mutationFn: async (params: CancelBookingParams) => {
      return await bookingService.cancelBooking(params.bookingId, { waive: params.waive, waiveNote: params.waiveNote });
    },
    onSuccess: (cancelledBooking, variables) => {
      // ["bookings"] prefix-matches calendar/list/room/count variants.
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["booking", variables.bookingId] });
      queryClient.invalidateQueries({ queryKey: ["booking-counts"] });
      queryClient.invalidateQueries({ queryKey: ["member-bookings"] });
      // Seed the just-cancelled booking into the detail cache if the server returned it.
      if (cancelledBooking?.id) {
        queryClient.setQueryData(["booking", cancelledBooking.id], cancelledBooking);
      }
    },
  });
}
